/**
 * Shared Orca archive wire schemas (OL-152).
 *
 * Serializable Effect `Schema` types shared by three peers:
 *
 *  1. **`AdminOrcaArchiveRpcs`** (`rpc/admin/orca-archive.rpc.ts`) — the
 *     operator door, which additionally serves raw bytes,
 *  2. **`OrcaloopsArchiveRpcs`** (`rpc/authenticated/orcaloops-archive.rpc.ts`)
 *     — the customer door, redacted and byte-less, and
 *  3. the **web clients** that call both.
 *
 * Because both `.rpc.ts` files are `export *`-ed through their own index barrel
 * into `rpc/index.ts`, a schema declared in BOTH would be a TS2308 duplicate
 * export under `bun check`. Every payload, success shape and archive-specific
 * error therefore lives here exactly once and neither group declares a schema
 * of its own.
 *
 * ## Self-contained on purpose
 *
 * This module imports `effect` and nothing else. It deliberately does NOT reach
 * into `services/orca/archive-layout.ts`, `archive-owner.ts` or
 * `OrcaArchiveReader.ts` — the wire contract is frozen independently of the
 * reader, so the object-class literal is re-declared here rather than imported.
 * That is what lets the handler seam and the UI seams be built in parallel
 * against a contract that cannot shift underneath them.
 *
 * ## Tenancy
 *
 * **No payload here carries a `customerId`.** The customer door takes an
 * `identifier` only; ownership is resolved server-side from the authenticated
 * actor, which makes the classic "trust the customer id on the wire" mistake
 * unrepresentable rather than merely unchecked.
 *
 * @since 1.0.0
 */
import { Schema } from "effect";

// ============================================================================
// Object classification
// ============================================================================

/**
 * How an archived object may be disclosed.
 *
 *  - `customer` — safe to surface on the customer door (after redaction).
 *  - `admin` — operator-only; never listed or read by a customer.
 *  - `never` — disclosed to nobody over the wire, on either door.
 *
 * Declared here rather than imported from the archive layout module so this
 * wire contract stays self-contained.
 *
 * @since 1.0.0
 */
export const ArchiveObjectClass = Schema.Literal("customer", "admin", "never");
export type ArchiveObjectClass = Schema.Schema.Type<typeof ArchiveObjectClass>;

// ============================================================================
// Read window
// ============================================================================

/**
 * A line window into a rendered text object: `offset` lines skipped from the
 * start, at most `limit` lines returned.
 *
 * @since 1.0.0
 */
export const ArchiveWindow = Schema.Struct({
  offset: Schema.Number,
  limit: Schema.Number,
});
export type ArchiveWindow = Schema.Schema.Type<typeof ArchiveWindow>;

// ============================================================================
// Listing
// ============================================================================

/**
 * One archived object in a run's listing.
 *
 * `renderable` says whether `*.archive.text` can project this object into
 * lines; a non-renderable object is download-only (and download is admin-only).
 *
 * @since 1.0.0
 */
export const OrcaArchiveObject = Schema.Struct({
  name: Schema.String,
  sizeBytes: Schema.Number,
  lastModified: Schema.DateTimeUtc,
  objectClass: ArchiveObjectClass,
  renderable: Schema.Boolean,
});
export type OrcaArchiveObject = Schema.Schema.Type<typeof OrcaArchiveObject>;

/**
 * One archived run: the agent that produced it, which attempt it was, and the
 * objects it left behind.
 *
 * `run` is the run key as it appears under the identifier (e.g. `"builder-1"`).
 *
 * @since 1.0.0
 */
export const OrcaArchiveRun = Schema.Struct({
  run: Schema.String,
  agent: Schema.String,
  attempt: Schema.Number,
  objects: Schema.Array(OrcaArchiveObject),
  totalBytes: Schema.Number,
});
export type OrcaArchiveRun = Schema.Schema.Type<typeof OrcaArchiveRun>;

/**
 * Everything archived under one identifier, grouped by run.
 *
 * Carries the `identifier` back so a client rendering several listings cannot
 * mis-attribute one to another.
 *
 * @since 1.0.0
 */
export const OrcaArchiveListing = Schema.Struct({
  identifier: Schema.String,
  runs: Schema.Array(OrcaArchiveRun),
});
export type OrcaArchiveListing = Schema.Schema.Type<typeof OrcaArchiveListing>;

// ============================================================================
// Text projection
// ============================================================================

/**
 * A rendered window of an archived object.
 *
 * `firstLineNumber` is the 1-based line number of `lines[0]` in the WHOLE
 * object, so a windowed read still numbers correctly. `truncated` flags that
 * more lines exist beyond the window, and `omittedRecords` counts records the
 * projection dropped (e.g. redacted or unrenderable records) so the omission is
 * visible rather than silent.
 *
 * @since 1.0.0
 */
export const OrcaArchiveText = Schema.Struct({
  lines: Schema.Array(Schema.String),
  firstLineNumber: Schema.Number,
  totalLines: Schema.Number,
  truncated: Schema.Boolean,
  omittedRecords: Schema.Number,
  byteSize: Schema.Number,
});
export type OrcaArchiveText = Schema.Schema.Type<typeof OrcaArchiveText>;

// ============================================================================
// Input payloads
// ============================================================================

/**
 * Input for both doors' `list`: the identifier to list.
 *
 * **No `customerId`.** On the customer door the owning customer is resolved
 * from the authenticated actor, never taken from the caller.
 *
 * @since 1.0.0
 */
export const ArchiveListInput = Schema.Struct({
  identifier: Schema.String,
});
export type ArchiveListInput = Schema.Schema.Type<typeof ArchiveListInput>;

/**
 * A single archived object addressed by identifier + run + object name. Used by
 * the admin-only `downloadUrl`.
 *
 * @since 1.0.0
 */
export const ArchiveObjectRef = Schema.Struct({
  identifier: Schema.String,
  run: Schema.String,
  object: Schema.String,
});
export type ArchiveObjectRef = Schema.Schema.Type<typeof ArchiveObjectRef>;

/**
 * Input for both doors' `text`: an object reference plus the line window to
 * render. Same tenancy rule as {@link ArchiveListInput} — no customer id.
 *
 * @since 1.0.0
 */
export const ArchiveTextInput = Schema.Struct({
  identifier: Schema.String,
  run: Schema.String,
  object: Schema.String,
  window: ArchiveWindow,
});
export type ArchiveTextInput = Schema.Schema.Type<typeof ArchiveTextInput>;

/**
 * A short-lived pre-signed URL for an archived object's raw bytes, and the
 * moment it stops working. **Admin-only** — raw bytes never cross the customer
 * door.
 *
 * @since 1.0.0
 */
export const OrcaArchiveDownloadUrl = Schema.Struct({
  url: Schema.String,
  expiresAt: Schema.DateTimeUtc,
});
export type OrcaArchiveDownloadUrl = Schema.Schema.Type<typeof OrcaArchiveDownloadUrl>;

// ============================================================================
// Errors
// ============================================================================

/**
 * The archive cannot answer right now.
 *
 * This error is PERMANENT in every archive error union, not a placeholder: it
 * is the honest failure both when the seam is not yet wired and when the reader
 * has no AWS configuration to read from. Replacing a stub handler body with a
 * real one is therefore not a wire change.
 *
 * @since 1.0.0
 */
export class ArchiveNotReady extends Schema.TaggedError<ArchiveNotReady>()(
  "ArchiveNotReady",
  {
    reason: Schema.String,
  },
) {}

/**
 * The requested object is too large to render into lines. Carries the object's
 * `byteSize` and the `limitBytes` ceiling so the UI can say how far over it is
 * and offer the download path instead (admin door only).
 *
 * @since 1.0.0
 */
export class ArchiveObjectTooLarge extends Schema.TaggedError<ArchiveObjectTooLarge>()(
  "ArchiveObjectTooLarge",
  {
    object: Schema.String,
    byteSize: Schema.Number,
    limitBytes: Schema.Number,
  },
) {}

/**
 * The requested object has no text projection — it is bytes, not records, so
 * `*.archive.text` cannot render it (its listing entry carries
 * `renderable: false`).
 *
 * @since 1.0.0
 */
export class ArchiveObjectNotRenderable extends Schema.TaggedError<ArchiveObjectNotRenderable>()(
  "ArchiveObjectNotRenderable",
  {
    object: Schema.String,
  },
) {}
